import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { BucketListItem } from '../types';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: linear-gradient(
      135deg,
      rgba(255, 245, 230, 0.98),
      rgba(255, 255, 255, 0.95)
    ),
    radial-gradient(
      ellipse at top,
      rgba(255, 179, 217, 0.1) 0%,
      transparent 50%
    );
  border: 3px solid rgba(255, 105, 180, 0.4);
  border-radius: 20px;
  padding: 2rem;
  padding-right: 1.5rem;
  max-width: 550px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 20px 60px rgba(255, 179, 217, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  font-family: 'Comfortaa', sans-serif;
  backdrop-filter: blur(10px);

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 179, 217, 0.1);
    border-radius: 10px;
    margin: 1rem 0;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      135deg,
      rgba(255, 179, 217, 0.6),
      rgba(212, 179, 255, 0.6)
    );
    border-radius: 10px;
    border: 2px solid rgba(255, 245, 230, 0.8);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 105, 180, 0.8),
      rgba(186, 85, 211, 0.8)
    );
  }

  /* Firefox scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 179, 217, 0.6) rgba(255, 179, 217, 0.1);

  /* Dreamy pastel texture */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 30%,
        rgba(255, 179, 217, 0.08) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 70%,
        rgba(212, 179, 255, 0.06) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 40% 80%,
        rgba(179, 255, 217, 0.05) 0%,
        transparent 50%
      );
    pointer-events: none;
    border-radius: 17px;
  }
`;

const ModalHeader = styled.div`
  position: relative;
  margin-bottom: 2rem;
  text-align: center;

  &::after {
    content: '✦ ✦ ✦';
    display: block;
    text-align: center;
    color: #da70d6;
    font-size: 1.2rem;
    margin-top: 0.5rem;
    letter-spacing: 0.5rem;
    opacity: 0.7;
  }
`;

const ModalTitle = styled.h1`
  margin: 0;
  color: #8b5a8c;
  font-family: 'Comfortaa', sans-serif;
  font-size: 2rem;
  font-weight: 600;
  text-shadow: 2px 2px 8px rgba(255, 105, 180, 0.3);
  position: relative;
  background: linear-gradient(135deg, #ff69b4, #da70d6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  &::before {
    content: '✨';
    font-size: 1.5rem;
    position: absolute;
    left: -2rem;
    top: 0.1rem;
    animation: sparkle 2s ease-in-out infinite;
  }

  &::after {
    content: '✨';
    font-size: 1.5rem;
    position: absolute;
    right: -2rem;
    top: 0.1rem;
    animation: sparkle 2s ease-in-out infinite reverse;
  }

  @keyframes sparkle {
    0%,
    100% {
      opacity: 0.7;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.6),
    rgba(212, 179, 255, 0.4)
  );
  border: 2px solid rgba(255, 105, 180, 0.3);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 16px;
  color: #8b5a8c;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  font-family: 'Comfortaa', sans-serif;
  font-weight: bold;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 105, 180, 0.8),
      rgba(186, 85, 211, 0.6)
    );
    border-color: rgba(255, 20, 147, 0.6);
    transform: scale(1.1);
    color: white;
  }
`;

const ModalBody = styled.div`
  color: #7a6b8a;
  line-height: 1.7;
  font-size: 1rem;
  position: relative;
  z-index: 1;
`;

const JournalHeader = styled.h2`
  color: #8b5a8c;
  font-family: 'Comfortaa', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 1.5rem 0 0.5rem 0;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 2px;
    background: linear-gradient(to right, #ff69b4, transparent);
    margin-top: 0.25rem;
  }
`;

const DateStamp = styled.div`
  color: #ba55d3;
  font-family: 'Comfortaa', sans-serif;
  font-style: italic;
  font-size: 0.9rem;
  margin-bottom: 1rem;

  &::before {
    content: '>> ';
    margin-right: 0.3rem;
  }
`;

const Description = styled.p`
  color: #7a6b8a;
  font-family: 'Comfortaa', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  margin: 1rem 0;
  text-align: justify;

  &:first-letter {
    font-size: 1.3em;
    font-weight: bold;
    color: #8b5a8c;
  }
`;

const PhotoGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;

  &::before {
    content: '';
    grid-column: 1 / -1;
    height: 1px;
    background: linear-gradient(to right, transparent, #da70d6, transparent);
    margin-bottom: 0.5rem;
  }
`;

const PhotoItem = styled.div`
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.2),
    rgba(212, 179, 255, 0.1)
  );
  border: 4px solid rgba(255, 179, 217, 0.3);
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(255, 105, 180, 0.1),
    inset 0 0 0 1px rgba(255, 179, 217, 0.2);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  transform: rotate(-1deg);
  overflow: hidden;

  &:nth-of-type(even) {
    transform: rotate(1deg);
  }

  &:hover {
    transform: rotate(0deg) scale(1.05);
    box-shadow: 0 8px 25px rgba(255, 179, 217, 0.4);
    border-color: rgba(255, 105, 180, 0.6);
    z-index: 10;
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const PhotoCaption = styled.div`
  padding: 0.75rem;
  font-family: 'Comfortaa', sans-serif;
  font-style: italic;
  color: #8b5a8c;
  font-size: 0.85rem;
  text-align: center;
  border-top: 1px solid rgba(255, 105, 180, 0.3);
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
`;

const BlogSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 240, 245, 0.8),
    rgba(230, 230, 250, 0.6)
  );
  border-left: 4px solid #da70d6;
  border-radius: 15px;
  padding: 1.5rem;
  margin: 2rem 0;
  position: relative;
  font-family: 'Comfortaa', sans-serif;
`;

const BlogAuthor = styled.div`
  color: #8b5a8c;
  font-family: 'Comfortaa', sans-serif;
  font-size: 1.1rem;
  font-style: italic;
  margin-bottom: 0.75rem;
  font-weight: bold;

  &::before {
    content: 'Personal notes from ';
    font-style: normal;
    font-weight: normal;
    color: #ba55d3;
  }
`;

const BlogContent = styled.div`
  color: #7a6b8a;
  font-size: 1rem;
  line-height: 1.6;
  font-style: italic;
  text-align: justify;

  &::before {
    content: '"';
    font-size: 1.5rem;
    color: #da70d6;
    line-height: 0;
  }

  &::after {
    content: '"';
    font-size: 1.5rem;
    color: #da70d6;
    line-height: 0;
  }
`;

const CompletionBadge = styled.div`
  display: inline-block;
  background: linear-gradient(
    135deg,
    rgba(179, 255, 217, 0.8),
    rgba(144, 238, 144, 0.6)
  );
  color: #5d4e75;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-family: 'Comfortaa', sans-serif;
  font-weight: bold;
  font-size: 1rem;
  margin-top: 2rem;
  border: 2px solid rgba(144, 238, 144, 0.6);
  box-shadow: 0 8px 25px rgba(144, 238, 144, 0.3);
  text-align: center;

  &::before {
    content: '✨ ';
    margin-right: 0.5rem;
  }

  &::after {
    content: ' ✨';
    margin-left: 0.5rem;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(10px);
`;

const LightboxContent = styled.div`
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  padding: 0 80px; /* Space for navigation arrows */

  @media (max-width: 768px) {
    padding: 0 60px;
  }
`;

const NavButton = styled.button<{ direction: 'prev' | 'next' }>`
  position: absolute;
  top: 50%;
  ${props => (props.direction === 'prev' ? 'left: 1rem;' : 'right: 1rem;')}
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  cursor: pointer;
  font-size: 28px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-family: 'Comfortaa', sans-serif;
  font-weight: bold;
  z-index: 2002;
  backdrop-filter: blur(8px);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-50%) scale(1.05);
    color: rgba(255, 255, 255, 1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 24px;
  }
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const LightboxCaption = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.95),
    rgba(212, 179, 255, 0.9)
  );
  padding: 1rem 2rem;
  border-radius: 15px;
  font-family: 'Comfortaa', sans-serif;
  color: #5d4e75;
  font-size: 1.1rem;
  text-align: center;
  max-width: 600px;
  border: 2px solid rgba(255, 105, 180, 0.5);
  box-shadow: 0 8px 25px rgba(255, 179, 217, 0.4);
`;

const ImageCounter = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.95),
    rgba(212, 179, 255, 0.9)
  );
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-family: 'Comfortaa', sans-serif;
  color: #5d4e75;
  font-size: 0.9rem;
  font-weight: bold;
  border: 2px solid rgba(255, 105, 180, 0.5);
  z-index: 2001;
`;

const LightboxCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.9),
    rgba(212, 179, 255, 0.8)
  );
  border: 2px solid rgba(255, 105, 180, 0.6);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  cursor: pointer;
  font-size: 24px;
  color: #5d4e75;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  font-family: 'Comfortaa', sans-serif;
  font-weight: bold;
  z-index: 2001;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 105, 180, 0.95),
      rgba(186, 85, 211, 0.9)
    );
    border-color: rgba(255, 20, 147, 0.8);
    transform: scale(1.1) rotate(90deg);
    color: white;
  }
`;

interface CompletionModalProps {
  item: BucketListItem;
  isOpen: boolean;
  onClose: () => void;
}

const CompletionModal: React.FC<CompletionModalProps> = ({
  item,
  isOpen,
  onClose,
}) => {
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    caption: string;
    index: number;
  } | null>(null);

  // Get all images with proper typing
  const allImages = useMemo(
    () =>
      item.images?.map((image, index) => ({
        url: typeof image === 'string' ? image : image.url,
        caption:
          typeof image === 'string'
            ? `Memory #${index + 1}`
            : image.caption || `Memory #${index + 1}`,
        index,
      })) || [],
    [item.images]
  );

  const navigateNext = useCallback(() => {
    if (!lightboxImage || allImages.length === 0) return;
    const nextIndex = (lightboxImage.index + 1) % allImages.length;
    setLightboxImage(allImages[nextIndex]);
  }, [lightboxImage, allImages]);

  const navigatePrev = useCallback(() => {
    if (!lightboxImage || allImages.length === 0) return;
    const prevIndex =
      lightboxImage.index === 0
        ? allImages.length - 1
        : lightboxImage.index - 1;
    setLightboxImage(allImages[prevIndex]);
  }, [lightboxImage, allImages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Escape key closes modals
      if (e.key === 'Escape') {
        if (lightboxImage) {
          setLightboxImage(null);
        } else {
          onClose();
        }
        return;
      }

      // Arrow keys navigate in lightbox
      if (lightboxImage && allImages.length > 1) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          navigatePrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          navigateNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    lightboxImage,
    allImages.length,
    onClose,
    navigateNext,
    navigatePrev,
  ]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLightboxOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setLightboxImage(null);
    }
  };

  const formatCompletedAt = (completedAt: string | undefined) => {
    if (!completedAt) return 'Not recorded';
    // Since completedAt is now a free-form text field, just return it as is
    return completedAt;
  };

  return (
    <>
      <ModalOverlay onClick={handleOverlayClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>×</CloseButton>

          <ModalHeader>
            <ModalTitle>{item.title}</ModalTitle>
          </ModalHeader>

          <ModalBody>
            <DateStamp>{formatCompletedAt(item.completedAt)}</DateStamp>

            {item.description && <Description>{item.description}</Description>}

            {item.images && item.images.length > 0 && (
              <>
                <JournalHeader>Captured Memories</JournalHeader>
                <PhotoGallery>
                  {allImages.map(image => (
                    <PhotoItem
                      key={image.index}
                      onClick={() => setLightboxImage(image)}
                    >
                      <PhotoImage src={image.url} alt={image.caption} />
                      <PhotoCaption>{image.caption}</PhotoCaption>
                    </PhotoItem>
                  ))}
                </PhotoGallery>
              </>
            )}

            {item.blogText && (
              <BlogSection>
                <BlogAuthor>aria</BlogAuthor>
                <BlogContent>{item.blogText}</BlogContent>
              </BlogSection>
            )}

            <CompletionBadge>Adventure Completed</CompletionBadge>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>

      {lightboxImage && (
        <LightboxOverlay onClick={handleLightboxOverlayClick}>
          <LightboxCloseButton onClick={() => setLightboxImage(null)}>
            ×
          </LightboxCloseButton>
          {allImages.length > 1 && (
            <ImageCounter>
              {lightboxImage.index + 1} / {allImages.length}
            </ImageCounter>
          )}
          <LightboxContent>
            {allImages.length > 1 && (
              <>
                <NavButton
                  direction="prev"
                  onClick={navigatePrev}
                  disabled={false}
                  aria-label="Previous image"
                >
                  ‹
                </NavButton>
                <NavButton
                  direction="next"
                  onClick={navigateNext}
                  disabled={false}
                  aria-label="Next image"
                >
                  ›
                </NavButton>
              </>
            )}
            <LightboxImage
              src={lightboxImage.url}
              alt={lightboxImage.caption}
            />
            <LightboxCaption>{lightboxImage.caption}</LightboxCaption>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </>
  );
};

export default CompletionModal;
